import React, { createContext, useContext, useState, useRef, useCallback, useEffect } from 'react';
import { io } from 'socket.io-client';
import api from '../utils/api';

const RemoteAssistContext = createContext(null);
export const useRemoteAssist = () => useContext(RemoteAssistContext);

const FALLBACK_ICE_CONFIG = {
  iceServers: [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
  ],
};

export function RemoteAssistProvider({ children }) {
  const [activeSession, setActiveSession] = useState(null);
  const [state, setState] = useState('idle'); // idle | loading | waiting | connected | ended
  const [isMuted, setIsMuted] = useState(false); // start unmuted
  const [isVideoOff, setIsVideoOff] = useState(false);
  const [clientMuted, setClientMuted] = useState(false);
  const [clientVideoOff, setClientVideoOff] = useState(false);
  const [clientScreenSharing, setClientScreenSharing] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [remoteStream, setRemoteStream] = useState(null); // webcam stream
  const [remoteScreenStream, setRemoteScreenStream] = useState(null); // screen share stream
  const [localStream, setLocalStream] = useState(null); // agent's webcam preview
  const [availableCameras, setAvailableCameras] = useState([]);
  const [selectedCameraId, setSelectedCameraId] = useState('');

  const socketRef = useRef(null);
  const pcRef = useRef(null);
  const localMediaRef = useRef(null); // webcam + mic
  const webcamSenderRef = useRef(null); // RTCRtpSender for agent's webcam video track
  const timerRef = useRef(null);
  const recorderRef = useRef(null);
  const chunkIndexRef = useRef(0);
  const chunkUploadQueueRef = useRef(Promise.resolve()); // serialize chunk uploads
  const pendingCandidates = useRef([]);
  const sessionRef = useRef(null);
  const iceConfigRef = useRef(FALLBACK_ICE_CONFIG);
  const stateRef = useRef('idle'); // mirror state in ref for stable closures
  const remoteStreamsRef = useRef(new Map()); // track streams by id
  const recordingStartedRef = useRef(false); // record only the first (webcam) stream

  // Keep stateRef in sync
  useEffect(() => { stateRef.current = state; }, [state]);

  // ── Cleanup ──
  const cleanupConnection = useCallback(() => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      try { recorderRef.current.stop(); } catch {}
    }
    recordingStartedRef.current = false;
    chunkIndexRef.current = 0;
    chunkUploadQueueRef.current = Promise.resolve();
    if (pcRef.current) { pcRef.current.close(); pcRef.current = null; }
    if (localMediaRef.current) { localMediaRef.current.getTracks().forEach(t => t.stop()); localMediaRef.current = null; }
    if (socketRef.current) { socketRef.current.disconnect(); socketRef.current = null; }
    setRemoteStream(null);
    setRemoteScreenStream(null);
    setLocalStream(null);
    setAvailableCameras([]);
    setSelectedCameraId('');
    webcamSenderRef.current = null;
    remoteStreamsRef.current.clear();
  }, []);

  // ── Join a session ──
  const joinSession = useCallback(async (sessionId) => {
    // Already in this session and connection is live — do nothing
    // Use ref to avoid stale closure on state
    if (sessionRef.current?.id === sessionId && stateRef.current !== 'idle' && stateRef.current !== 'ended') {
      return;
    }

    cleanupConnection();
    setState('loading');
    setElapsed(0);
    setClientMuted(false);
    setClientVideoOff(false);
    setClientScreenSharing(false);
    setIsMuted(false);
    setIsVideoOff(false);

    try {
      const { data } = await api.get(`/remote-assist/sessions/${sessionId}`);
      setActiveSession(data);
      sessionRef.current = data;

      if (data.status === 'ended') { setState('ended'); return; }

      // Get agent's webcam + mic (unmuted by default)
      let mediaStream = null;
      try {
        mediaStream = await navigator.mediaDevices.getUserMedia({
          video: { width: { ideal: 640 }, height: { ideal: 480 }, facingMode: 'user' },
          audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
        });
      } catch {
        // Fallback: audio only
        try {
          mediaStream = await navigator.mediaDevices.getUserMedia({
            audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
          });
          setIsVideoOff(true);
        } catch {}
      }
      localMediaRef.current = mediaStream;
      if (mediaStream) setLocalStream(mediaStream);

      // Enumerate cameras (labels become available after permission granted)
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const cams = devices.filter(d => d.kind === 'videoinput');
        setAvailableCameras(cams);
        const currentId = mediaStream?.getVideoTracks?.()[0]?.getSettings?.().deviceId || '';
        if (currentId) setSelectedCameraId(currentId);
      } catch {}

      // Fetch dynamic ICE config (managed TURN, when configured) — never block the call on this.
      api.get('/remote-assist/ice-config').then(({ data }) => {
        if (data?.iceServers?.length) iceConfigRef.current = data;
      }).catch(() => {});

      const authToken = localStorage.getItem('accessToken');
      const socket = io('/remote-assist', {
        auth: { token: authToken },
        transports: ['websocket'],
        reconnection: true,
      });
      socketRef.current = socket;

      socket.on('connect', () => {
        socket.emit('join-session', { token: data.token });
        setState('waiting');
      });

      socket.on('offer', async ({ offer }) => {
        await handleOffer(offer, data.token, socket);
      });

      socket.on('ice-candidate', async ({ candidate }) => {
        if (pcRef.current && pcRef.current.remoteDescription) {
          await pcRef.current.addIceCandidate(new RTCIceCandidate(candidate));
        } else {
          pendingCandidates.current.push(candidate);
        }
      });

      socket.on('session-ended', () => {
        setState('ended');
        stopRecording(data.id);
      });

      socket.on('peer-joined', ({ type }) => {
        if (type === 'customer') {
          // Show toast with link to session
          const toastImport = require('react-hot-toast');
          const toastFn = toastImport.default || toastImport;
          toastFn.success('Cliente conectado a Live Assist', { duration: 8000, icon: '🖥️' });
        }
      });

      socket.on('peer-left', ({ type }) => {
        if (type === 'customer') {
          setState('ended');
          stopRecording(data.id);
        }
      });

      socket.on('peer-mute', ({ type, muted }) => {
        if (type === 'customer') setClientMuted(muted);
      });

      socket.on('peer-video', ({ type, videoOff }) => {
        if (type === 'customer') setClientVideoOff(videoOff);
      });

      socket.on('peer-screen-share', ({ type, sharing }) => {
        if (type === 'customer') setClientScreenSharing(sharing);
      });

      socket.on('connect_error', () => {});
    } catch {
      setState('ended');
    }
  }, [cleanupConnection]);

  // ── Handle WebRTC offer (customer initiates) ──
  async function handleOffer(offer, sessionToken, socket) {
    // Close existing PC if renegotiating
    if (pcRef.current) {
      // Renegotiation: just set remote description and create answer
      try {
        await pcRef.current.setRemoteDescription(new RTCSessionDescription(offer));
        const answer = await pcRef.current.createAnswer();
        await pcRef.current.setLocalDescription(answer);
        socket.emit('answer', { token: sessionToken, answer });
        return;
      } catch {
        // If renegotiation fails, create fresh PC
        pcRef.current.close();
        pcRef.current = null;
      }
    }

    const pc = new RTCPeerConnection(iceConfigRef.current);
    pcRef.current = pc;

    // Handle remote tracks (customer's webcam + optional screen)
    pc.ontrack = (e) => {
      const stream = e.streams[0];
      if (!stream) return;

      // Store stream reference
      remoteStreamsRef.current.set(stream.id, stream);

      // Heuristic: first stream is webcam, additional streams are screen share
      const allStreams = Array.from(remoteStreamsRef.current.values());
      if (allStreams.length === 1) {
        setRemoteStream(stream);
        // Start recording the webcam stream ONCE.
        // We deliberately do NOT rebuild the recorder when screen share starts/stops —
        // mixing chunks from different MediaRecorder instances (with different codecs/
        // track IDs) produces a webm file that ffmpeg concat cannot play back.
        if (!recordingStartedRef.current) {
          recordingStartedRef.current = true;
          startRecordingSilent(stream);
        }
      } else {
        // The newest stream is likely the screen share
        setRemoteScreenStream(stream);
      }

      // Listen for tracks being removed (screen share stops)
      stream.onremovetrack = () => {
        if (stream.getTracks().length === 0) {
          remoteStreamsRef.current.delete(stream.id);
          // Re-evaluate which stream is which
          const remaining = Array.from(remoteStreamsRef.current.values());
          if (remaining.length <= 1) {
            setRemoteScreenStream(null);
            if (remaining[0]) setRemoteStream(remaining[0]);
          }
        }
      };
    };

    pc.onicecandidate = (e) => {
      if (e.candidate) {
        socket.emit('ice-candidate', { token: sessionToken, candidate: e.candidate });
      }
    };

    pc.onconnectionstatechange = () => {
      if (pc.connectionState === 'connected') {
        setState('connected');
        if (!timerRef.current) {
          const start = Date.now();
          timerRef.current = setInterval(() => setElapsed(Math.floor((Date.now() - start) / 1000)), 1000);
        }
      }
      if (pc.connectionState === 'failed' || pc.connectionState === 'disconnected') {
        setState('ended');
      }
    };

    // Add agent's local media tracks (webcam + mic, enabled = unmuted)
    if (localMediaRef.current) {
      localMediaRef.current.getTracks().forEach(t => {
        const sender = pc.addTrack(t, localMediaRef.current);
        if (t.kind === 'video') webcamSenderRef.current = sender;
      });
    }

    await pc.setRemoteDescription(new RTCSessionDescription(offer));
    for (const c of pendingCandidates.current) {
      await pc.addIceCandidate(new RTCIceCandidate(c));
    }
    pendingCandidates.current = [];

    const answer = await pc.createAnswer();
    await pc.setLocalDescription(answer);
    socket.emit('answer', { token: sessionToken, answer });
  }

  // ── Mic toggle ──
  const toggleMic = useCallback(() => {
    if (localMediaRef.current) {
      const track = localMediaRef.current.getAudioTracks()[0];
      if (track) {
        track.enabled = !track.enabled;
        const muted = !track.enabled;
        setIsMuted(muted);
        const s = sessionRef.current;
        if (s) socketRef.current?.emit('mute-toggle', { token: s.token, muted });
      }
    }
  }, []);

  // ── Switch camera (for agents with multiple videoinputs) ──
  const switchCamera = useCallback(async (deviceId) => {
    if (!deviceId || !localMediaRef.current) return;
    try {
      const newStream = await navigator.mediaDevices.getUserMedia({
        video: { deviceId: { exact: deviceId }, width: { ideal: 640 }, height: { ideal: 480 } },
      });
      const newTrack = newStream.getVideoTracks()[0];
      if (!newTrack) return;
      if (webcamSenderRef.current) {
        try { await webcamSenderRef.current.replaceTrack(newTrack); } catch {}
      }
      const oldTrack = localMediaRef.current.getVideoTracks()[0];
      if (oldTrack) {
        localMediaRef.current.removeTrack(oldTrack);
        oldTrack.stop();
      }
      localMediaRef.current.addTrack(newTrack);
      // Force local preview to pick up the new track
      setLocalStream(new MediaStream(localMediaRef.current.getTracks()));
      setSelectedCameraId(deviceId);
    } catch (err) {
      console.error('[RemoteAssist] Failed to switch camera:', err);
    }
  }, []);

  // ── Video toggle ──
  const toggleVideo = useCallback(() => {
    if (localMediaRef.current) {
      const track = localMediaRef.current.getVideoTracks()[0];
      if (track) {
        track.enabled = !track.enabled;
        const videoOff = !track.enabled;
        setIsVideoOff(videoOff);
        const s = sessionRef.current;
        if (s) socketRef.current?.emit('video-toggle', { token: s.token, videoOff });
      }
    }
  }, []);

  // ── Chunked recording — uploads every 30s to server ──
  function uploadChunk(blob, sessionId, index) {
    const formData = new FormData();
    formData.append('chunk', blob, `chunk-${index}.webm`);
    formData.append('index', String(index));
    return api.post(`/remote-assist/sessions/${sessionId}/chunk`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 30000,
    }).catch((err) => {
      console.error(`[RemoteAssist] Chunk ${index} upload failed:`, err.message);
    });
  }

  function startRecordingSilent(stream) {
    if (!stream) return;
    const sid = sessionRef.current?.id;
    if (!sid) return;
    // Check stream has tracks to record
    if (stream.getTracks().length === 0) {
      console.warn('[RemoteAssist] No tracks in stream, skipping recording');
      return;
    }
    try {
      // Try codecs in order of preference
      const codecs = [
        'video/webm;codecs=vp9,opus',
        'video/webm;codecs=vp8,opus',
        'video/webm;codecs=vp8',
        'video/webm',
      ];
      const mimeType = codecs.find(c => MediaRecorder.isTypeSupported(c));
      if (!mimeType) {
        console.warn('[RemoteAssist] No supported recording codec found');
        return;
      }
      console.log(`[RemoteAssist] Recording with codec: ${mimeType}`);
      const recorder = new MediaRecorder(stream, {
        mimeType,
        videoBitsPerSecond: 500000,
        audioBitsPerSecond: 32000,
      });
      recorderRef.current = recorder;
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          const idx = chunkIndexRef.current++;
          const blob = e.data;
          // Serialize uploads to guarantee order
          chunkUploadQueueRef.current = chunkUploadQueueRef.current.then(() =>
            uploadChunk(blob, sid, idx)
          );
        }
      };
      recorder.onerror = (e) => {
        console.error('[RemoteAssist] MediaRecorder error:', e.error?.message || e);
      };
      // Request data every 30 seconds
      recorder.start(30000);
    } catch (err) {
      console.error('[RemoteAssist] Failed to start recording:', err.message);
    }
  }

  function stopRecording(sessionId) {
    return new Promise((resolve) => {
      if (!recorderRef.current || recorderRef.current.state === 'inactive') {
        resolve();
        return;
      }
      const sid = sessionId || sessionRef.current?.id;
      recorderRef.current.onstop = () => {
        if (sid) {
          // Wait for all chunks to finish uploading, then finalize
          chunkUploadQueueRef.current.then(() => {
            api.post(`/remote-assist/sessions/${sid}/recording/finalize`, {}, { timeout: 60000 })
              .catch(() => {});
          }).finally(resolve);
        } else {
          resolve();
        }
      };
      recorderRef.current.stop();
      // Safety timeout — don't block cleanup forever
      setTimeout(resolve, 15000);
    });
  }

  // ── End session ──
  const endSession = useCallback(async () => {
    const s = sessionRef.current;
    if (s) {
      socketRef.current?.emit('end-session', { token: s.token });
    }
    setState('ended');
    // Wait for recording to finish uploading before cleaning up connection
    await stopRecording();
    cleanupConnection();
  }, [cleanupConnection]);

  // ── Dismiss ──
  const dismiss = useCallback(() => {
    setActiveSession(null);
    sessionRef.current = null;
    setState('idle');
    setElapsed(0);
  }, []);

  // beforeunload: end session if agent closes tab (backend auto-finalizes recording)
  useEffect(() => {
    const handleBeforeUnload = () => {
      const s = sessionRef.current;
      if (s && socketRef.current) {
        socketRef.current.emit('end-session', { token: s.token });
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => { cleanupConnection(); };
  }, [cleanupConnection]);

  return (
    <RemoteAssistContext.Provider value={{
      activeSession, state, isMuted, isVideoOff, clientMuted, clientVideoOff,
      clientScreenSharing, elapsed, remoteStream, remoteScreenStream, localStream,
      availableCameras, selectedCameraId, switchCamera,
      joinSession, endSession, toggleMic, toggleVideo, dismiss,
    }}>
      {children}
    </RemoteAssistContext.Provider>
  );
}
